/**
 * Strings of the redesigned logistics views (`logistics.ux.*`): run states, cards, chart headlines, day groups.
 * Nested into `logisticsPl` / `logisticsEn` (see `logistics.ts`); `logisticsUxEn` is typed against `logisticsUxPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const logisticsUxPl = {
  runState: { done: 'Zakończony', active: 'W trasie', idle: 'Niewyjechany' },
  runsSummary: { done: 'zakończone: {count}', active: 'w trasie: {count}', idle: 'niewyjechane: {count}' },
  run: {
    failed: 'Nieudane: {count}',
    closed: 'zamknięte {done} z {total}',
    driver: 'Kierowca',
    vehicle: 'Pojazd',
    km: 'Dystans',
    cost: 'Koszt',
  },
  trend: {
    onTime: 'Terminowość',
    late: 'Spóźnione',
    failed: 'Nieudane',
    problemDays: 'Dni z problemami',
    problemDay: 'Dzień z problemami (≥ 10% dostaw spóźnionych lub nieudanych)',
  },
  routes: {
    runDaysHint: 'dni jazdy łącznie: {days}',
    onTimeShort: 'w terminie',
    mixAria: 'Dostawy: bez problemów {ok}, spóźnione {late}, nieudane {failed}',
    ok: 'Bez problemów',
    lateN: 'spóźnione: {count}',
    failedN: 'nieudane: {count}',
    viewLabel: 'Widok listy tras',
    viewCards: 'Karty',
    viewTable: 'Tabela',
  },
  list: {
    dayCount: 'dostaw: {count}',
    dayDelivered: 'dostarczone: {count}',
    dayProblems: 'problemy: {count}',
  },
  filters: { title: 'Zawęź' },
  bulk: { hint: 'Zmiana obejmie tylko dostawy, dla których jest dozwolona — pozostałe zostaną pominięte.' },
} as const;

export const logisticsUxEn: Shape<typeof logisticsUxPl> = {
  runState: { done: 'Finished', active: 'On the road', idle: 'Not departed' },
  runsSummary: { done: 'finished: {count}', active: 'on the road: {count}', idle: 'not departed: {count}' },
  run: {
    failed: 'Failed: {count}',
    closed: '{done} of {total} closed',
    driver: 'Driver',
    vehicle: 'Vehicle',
    km: 'Distance',
    cost: 'Cost',
  },
  trend: {
    onTime: 'On time',
    late: 'Late',
    failed: 'Failed',
    problemDays: 'Days with problems',
    problemDay: 'Day with problems (≥ 10% of deliveries late or failed)',
  },
  routes: {
    runDaysHint: 'driving days in total: {days}',
    onTimeShort: 'on time',
    mixAria: 'Deliveries: no problems {ok}, late {late}, failed {failed}',
    ok: 'No problems',
    lateN: 'late: {count}',
    failedN: 'failed: {count}',
    viewLabel: 'Routes list view',
    viewCards: 'Cards',
    viewTable: 'Table',
  },
  list: {
    dayCount: 'deliveries: {count}',
    dayDelivered: 'delivered: {count}',
    dayProblems: 'problems: {count}',
  },
  filters: { title: 'Narrow down' },
  bulk: { hint: 'Only deliveries the change is allowed for will be updated — the rest are skipped.' },
};
